import React from "react";
import type { MutableRefObject, RefObject } from "react";
import { type useI18nContext, Typography } from "@procore/core-react";
import { AuthManager2 } from "@/lib/managers/auth-manager-2";
import { SessionStore } from "@/stores/session-store.core";
import type { BryntumTaskBoardProps, BryntumTaskBoard } from "@bryntum/taskboard-react-thin";
import type { GanttTearsheetRef } from "../gantt/gantt-tearsheets";
import type { ProjectModel, TaskModel } from "@bryntum/taskboard-thin";
import { ResourceType } from "@/react/prop-types";
import {
   getAttachedDate,
   getDetachedDay,
   convertDetachedDayToUtcDateWithoutTimezoneOffset,
} from "@laborchart-modules/common/dist/datetime";
import { getLastWorkDate, getLastWorkDateInNumberOfWeeks } from "@/lib/utils/date-2";
import {
   editedTearsheetRequestToTask,
   editedTearsheetAssignmentToTask,
   editedTearsheetEquipmentAssignmentToTask,
   createdTearsheetAssignmentToTask,
   sortTasksByJobTitleSequence,
   tearsheetPersonToTaskCardFields,
} from "./utils";
import { BoardsTaskType } from "@laborchart-modules/lc-core-api/dist/api/assignments/load-boards";
import { AuthAction } from "@/react/providers/permission-context-provider";
import { formatDateTime } from "@/react/shared/helper";
import { BryntumModalType, showBryntumModal } from "../common/bryntum/bryntum-modals";
import { v4 } from "uuid";
import { PersonStore } from "@/stores/person-store.core";
import { reportError } from "@/utils/logging-utils";
import type { NotifiableError } from "@bugsnag/js";
import { publish } from "./hooks/boards-event-bus";
import { authManager } from "@/lib/managers/auth-manager";

// Helper type to extract just the Function type from event handlers.
export type BryntumFn<T extends keyof BryntumTaskBoardProps> = Extract<
   BryntumTaskBoardProps[T],
   (...args: any[]) => any
>;

type OnTaskEndAssignmentSingleProjectParams = {
   projectId: string;
   selectedTasks: TaskModel[];
   selectedDate: Date;
   taskBoardRef: RefObject<BryntumTaskBoard>;
   sendAssignmentAlertsTearsheetRef: RefObject<any>;
   canManageAlerts: boolean;
   I18n: ReturnType<typeof useI18nContext>;
};

type onTaskDeleteAssignmentSingleProjectParams = {
   projectId: string;
   selectedTasks: TaskModel[];
   taskBoardRef: RefObject<BryntumTaskBoard>;
   sendAssignmentAlertsTearsheetRef: RefObject<any>;
   canManageAlerts: boolean;
   I18n: ReturnType<typeof useI18nContext>;
};

type onTaskDeleteAssignmentMultipleProjectsParams = {
   selectedTasks: TaskModel[];
   taskBoardRef: RefObject<BryntumTaskBoard>;
   I18n: ReturnType<typeof useI18nContext>;
};

type OnTaskEndAssignmentMultipleProjectsParams = {
   selectedTasks: TaskModel[];
   selectedDate: Date;
   taskBoardRef: RefObject<BryntumTaskBoard>;
   I18n: ReturnType<typeof useI18nContext>;
};

export function endAssignments(
   assignments: TaskModel[],
   taskBoardRef: RefObject<BryntumTaskBoard>,
   effectiveDate: Date,
) {
   const assignmentsToManuallyRemove: TaskModel[] = [];
   assignments.forEach((task: TaskModel) => {
      const workDays = task.getData("work_days");
      const taskStartDate = getAttachedDate(task.getData("start_day"));
      const endDay = getLastWorkDate({
         fromDate: effectiveDate,
         workDays,
      });
      if (endDay && endDay < taskStartDate) {
         task.remove();
      } else {
         task.set({
            end_day: endDay ? getDetachedDay(endDay) : null,
         });
         assignmentsToManuallyRemove.push(task);
      }
   });
   (taskBoardRef.current?.instance as any).project.sync();
   (taskBoardRef.current?.instance as any).project.suspendAutoSync();
   deleteAssignments(assignmentsToManuallyRemove);
   (taskBoardRef.current?.instance as any).project.acceptChanges();
   (taskBoardRef.current?.instance as any).project.resumeAutoSync();
}

export function deleteAssignments(assignments: TaskModel[]) {
   assignments.forEach((task: TaskModel) => {
      task.remove();
   });
}

export async function transferAssignments(
   assignments: TaskModel[],
   targetColumn: any,
   viewingDay: number,
   newEndDay: number,
   taskBoardRef: RefObject<BryntumTaskBoard>,
   categoryId: string | null,
   subcategoryId: string | null,
   dropWeight?: number,
): Promise<Array<{ id: string; recipientIds: string[] }>> {
   const phantomIdToResourceMap: Array<{ phantomId: string; resourceId: string }> = [];
   const assignmentsToManuallyRemove: TaskModel[] = [];
   const effectiveDate = getAttachedDate(viewingDay);

   const project = taskBoardRef.current?.instance.project as ProjectModel | undefined;

   if (!project) {
      return [];
   }

   // Note: auto-sync is already suspended by onBeforeTaskDrop. Do NOT suspend again
   // or the counter will be imbalanced (suspend increments, resume decrements).

   assignments.forEach((task: TaskModel) => {
      if (task.getData("type") === BoardsTaskType.ASSIGNMENT) {
         const resourceId = task.getData("resource_id");

         // Generate a phantom ID that Bryntum will use to track this new task
         const phantomId = v4();

         // Pass 'id' field - Bryntum treats this as the phantom ID for new records
         // Spread all original task data and override transfer-specific fields
         const newTaskData = {
            // Copy all properties from the original task
            ...(task as any).data,
            // Override with new phantom ID and transfer-specific fields
            id: phantomId,
            project_id: targetColumn.id,
            start_day: viewingDay,
            end_day: newEndDay,
            start_time: targetColumn.getData("daily_start_time"),
            end_time: targetColumn.getData("daily_end_time"),
            // Apply category fields for the new assignment in the target project
            category_id: categoryId,
            subcategory_id: subcategoryId,
            // Preserve the drop position weight so the task appears where it was dropped
            ...(dropWeight !== undefined ? { weight: dropWeight } : {}),
         };

         const addedTask = (taskBoardRef.current?.instance as any).addTask(
            targetColumn as any,
            undefined,
            newTaskData,
         );

         // Bryntum's addTask returns data, not a model - find task in store and use .set()
         if (dropWeight !== undefined) {
            const taskStore = taskBoardRef.current?.instance?.project?.taskStore;
            const taskId = addedTask?.id || phantomId;
            if (taskStore && taskId) {
               const taskInStore = (taskStore as any).getById(taskId);
               if (taskInStore && typeof taskInStore.set === "function") {
                  taskInStore.set("weight", dropWeight);
               } else if (taskInStore) {
                  taskInStore.weight = dropWeight;
               }
            }
         }

         // Track phantom ID to resource mapping for after sync
         phantomIdToResourceMap.push({ phantomId, resourceId });

         // Truncate the old assignment (same logic as endAssignments)
         // Note: We do NOT change category fields on the truncated assignment
         const workDays = task.getData("work_days");
         const taskStartDate = getAttachedDate(task.getData("start_day"));
         const endDay = getLastWorkDate({
            fromDate: effectiveDate,
            workDays,
         });

         if (endDay && endDay < taskStartDate) {
            task.remove();
         } else {
            task.set({
               end_day: endDay ? getDetachedDay(endDay) : null,
            });
            assignmentsToManuallyRemove.push(task);
         }
      }
   });

   // Trigger a sort to reorder by weight - this will put the new task card in the correct category without requiring a refresh
   const taskStore = taskBoardRef.current?.instance?.project?.taskStore;
   if (taskStore) {
      (taskStore as any).sort({ field: "weight", ascending: true });
   }

   // Sync and wait for completion - response contains ID resolution mapping
   const response = await project.sync();

   // Clean up: remove truncated tasks from view
   deleteAssignments(assignmentsToManuallyRemove);
   project.acceptChanges();
   project.resumeAutoSync();

   sortTasksByJobTitleSequence(taskBoardRef);

   // Notify both source and target columns that their tasks changed
   const sourceProjectIds = new Set(
      assignments.map((t: any) => t.getData("_origProjectId") as string),
   );
   sourceProjectIds.forEach((projectId) => publish("column-tasks-changed", { projectId }));
   publish("column-tasks-changed", { projectId: targetColumn.id as string });

   // Map phantom IDs to real IDs from the sync response
   // Response structure: { response: { tasks: { rows: [{ id: realId, $PhantomId: phantomId }] } } }
   const idResolutionRows = response?.response?.tasks?.rows || [];

   const result = phantomIdToResourceMap.map(({ phantomId, resourceId }) => {
      const resolvedRow = idResolutionRows.find((row: any) => row.$PhantomId === phantomId);
      return {
         id: resolvedRow?.id || phantomId, // Fallback to phantomId if not found
         recipientIds: [resourceId],
      };
   });

   return result;
}

export function getEndAssignmentBody(
   selectedTasks: TaskModel[],
   selectedDate: Date,
   I18n: ReturnType<typeof useI18nContext>,
) {
   let formattedDate = "";
   let body = "";
   if (selectedTasks.length === 1) {
      const taskStartDate = getAttachedDate(selectedTasks[0].getData("start_day"));
      const newEffectiveDate = getLastWorkDate({
         fromDate: selectedDate,
         workDays: selectedTasks[0].getData("work_days"),
      });
      formattedDate = newEffectiveDate
         ? formatDateTime(newEffectiveDate, {
              locale: I18n.locale,
              isRequiredTimezone: false,
           })
         : "";
      if (newEffectiveDate && newEffectiveDate < taskStartDate) {
         body = I18n.t(
            "views.company.workforce_planning.boards.modals.end_assignment_body_remove_completely",
            {
               count: selectedTasks.length,
            },
         );
      } else {
         body = I18n.t("views.company.workforce_planning.boards.modals.end_assignment_body", {
            date: formattedDate,
            count: selectedTasks.length,
         });
      }
   } else {
      formattedDate = selectedDate
         ? formatDateTime(selectedDate, { locale: I18n.locale, isRequiredTimezone: false })
         : "";
      let anyAssignmentWillBeRemovedCompletely = false;
      selectedTasks.forEach((task: TaskModel) => {
         const taskStartDate = getAttachedDate(task.getData("start_day"));
         const newEffectiveDate = getLastWorkDate({
            fromDate: selectedDate,
            workDays: task.getData("work_days"),
         });
         if (newEffectiveDate && newEffectiveDate < taskStartDate) {
            anyAssignmentWillBeRemovedCompletely = true;
         }
      });

      body = anyAssignmentWillBeRemovedCompletely
         ? I18n.t(
              "views.company.workforce_planning.boards.modals.end_assignment_body_remove_completely",
              {
                 date: formattedDate,
                 count: selectedTasks.length,
              },
           )
         : I18n.t("views.company.workforce_planning.boards.modals.end_assignment_body", {
              date: formattedDate,
              count: selectedTasks.length,
           });
   }
   return body;
}

export function onTaskEndAssignmentSingleProject(params: OnTaskEndAssignmentSingleProjectParams) {
   const {
      projectId,
      selectedTasks,
      selectedDate,
      taskBoardRef,
      sendAssignmentAlertsTearsheetRef,
      canManageAlerts,
      I18n,
   } = params;
   const onSecondaryOptionClick = !canManageAlerts
      ? undefined
      : () => {
           const assignments = selectedTasks.map((task: any) => ({
              id: task.getData("id"),
              recipientIds: [task.getData("resource_id")],
           }));
           endAssignments(selectedTasks, taskBoardRef, selectedDate);
           publish("column-tasks-changed", { projectId });
           sendAssignmentAlertsTearsheetRef.current?.handleOpenTearsheet({
              projectId: projectId,
              alertType: "assignment-edit",
              assignments,
           });
        };

   showBryntumModal({
      type: BryntumModalType.EndOrDeleteAssignment,
      modalId: "boards-confirmation-modal-container",
      title: I18n.t("views.company.workforce_planning.boards.modals.end_assignment", {
         count: selectedTasks.length,
      }),
      body: getEndAssignmentBody(selectedTasks, selectedDate, I18n),
      onSave: () => {
         endAssignments(selectedTasks, taskBoardRef, selectedDate);
         publish("column-tasks-changed", { projectId });
      },
      onSecondaryOptionClick: onSecondaryOptionClick,
   });
}

export function onTaskDeleteAssignmentSingleProject(
   params: onTaskDeleteAssignmentSingleProjectParams,
) {
   const { projectId, selectedTasks, sendAssignmentAlertsTearsheetRef, canManageAlerts, I18n } =
      params;

   const onSecondaryOptionClick = !canManageAlerts
      ? undefined
      : () => {
           const assignments = selectedTasks.map((task: any) => ({
              id: task.getData("id"),
              recipientIds: [task.getData("resource_id")],
           }));
           deleteAssignments(selectedTasks);
           publish("column-tasks-changed", { projectId });
           sendAssignmentAlertsTearsheetRef.current?.handleOpenTearsheet({
              projectId: projectId,
              alertType: "assignment-delete",
              assignments,
           });
        };
   showBryntumModal({
      type: BryntumModalType.EndOrDeleteAssignment,
      modalId: "boards-confirmation-modal-container",
      title: I18n.t("views.company.workforce_planning.boards.modals.delete_assignment", {
         count: selectedTasks.length,
      }),
      body: I18n.t("views.company.workforce_planning.boards.modals.delete_assignment_body", {
         count: selectedTasks.length,
      }),
      onSave: () => {
         deleteAssignments(selectedTasks);
         publish("column-tasks-changed", { projectId });
      },
      onSecondaryOptionClick: onSecondaryOptionClick,
   });
}

export function onTaskEndAssignmentMultipleProjects(
   params: OnTaskEndAssignmentMultipleProjectsParams,
) {
   const { selectedTasks, taskBoardRef, I18n, selectedDate } = params;
   showBryntumModal({
      type: BryntumModalType.EndOrDeleteAssignment,
      modalId: "boards-confirmation-modal-container",
      title: I18n.t("views.company.workforce_planning.boards.modals.end_assignment", {
         count: selectedTasks.length,
      }),
      body: getEndAssignmentBody(selectedTasks, selectedDate, I18n),
      onSave: () => {
         endAssignments(selectedTasks, taskBoardRef, selectedDate);
         const projectIds = new Set(
            selectedTasks.map((task: any) => task.getData("project_id") as string),
         );
         projectIds.forEach((projectId) => publish("column-tasks-changed", { projectId }));
      },
   });
}

export function onTaskDeleteAssignmentMultipleProjects(
   params: onTaskDeleteAssignmentMultipleProjectsParams,
) {
   const { selectedTasks, I18n } = params;
   showBryntumModal({
      type: BryntumModalType.EndOrDeleteAssignment,
      modalId: "boards-confirmation-modal-container",
      title: I18n.t("views.company.workforce_planning.boards.modals.delete_assignment", {
         count: selectedTasks.length,
      }),
      body: I18n.t("views.company.workforce_planning.boards.modals.delete_assignment_body", {
         count: selectedTasks.length,
      }),
      onSave: () => {
         deleteAssignments(selectedTasks);
         const projectIds = new Set(
            selectedTasks.map((task: any) => task.getData("project_id") as string),
         );
         projectIds.forEach((projectId) => publish("column-tasks-changed", { projectId }));
      },
   });
}

// v7: TaskMenuItem type changed, use flexible type for menu items
type OnTaskMenuProcessItemsParams = {
   items: Record<string, any>;
   taskBoardRef: RefObject<BryntumTaskBoard>;
   selectedDay: number;
   I18n: ReturnType<typeof useI18nContext>;
   personDetailsTearsheetRef: RefObject<GanttTearsheetRef>;
   sendAssignmentAlertsTearsheetRef: RefObject<GanttTearsheetRef>;
   checkAuthAction: (action: AuthAction) => boolean;
};
export function onTaskMenuOnProcessItems(params: OnTaskMenuProcessItemsParams) {
   const {
      items,
      taskBoardRef,
      selectedDay,
      I18n,
      personDetailsTearsheetRef,
      sendAssignmentAlertsTearsheetRef,
      checkAuthAction,
   } = params;
   const selectedTasks = taskBoardRef.current?.instance.selectedTasks ?? [];
   const selectedDate = getAttachedDate(selectedDay);
   const selectedProjectIds = Array.from(
      new Set(selectedTasks.map((task: any) => task.getData("project_id"))),
   ) as string[];
   const selectedTaskTypes = Array.from(
      new Set(selectedTasks.map((task: any) => task.getData("type"))),
   );
   const canManageAssignments = checkAuthAction(AuthAction.MANAGE_ASSIGNMENTS);
   const canManageAlerts = checkAuthAction(AuthAction.MANAGE_ALERTS);
   if (selectedTasks.length === 1 && selectedTaskTypes[0] === BoardsTaskType.ASSIGNMENT) {
      const task = selectedTasks[0];
      const resourceName = task.getData("name");
      const resourceJobTitleName = task.getData("job_title_name");
      items.personInfo = {
         html: (
            <div className="task-menu-person-info">
               <span className="task-menu-person-info-name">{resourceName}</span>
               <span className="task-menu-person-info-job-title">
                  {resourceJobTitleName || "--"}
               </span>
            </div>
         ),
         weight: 100,
      };

      items.viewPerson = {
         text: I18n.t("views.company.workforce_planning.boards.task_menu.view_person"),
         onItem: () => {
            const resourceId = selectedTasks[0].getData("resource_id");
            personDetailsTearsheetRef.current?.handleOpenTearsheet({
               personId: resourceId,
               projectId: selectedProjectIds[0],
               callback: async () => {
                  try {
                     const lastNameFirst =
                        authManager.authedUser()?.preferences()?.displayLastNamesFirst() ?? false;
                     const response = await PersonStore.getPerson(resourceId).payload;
                     const person = response?.data;
                     if (!person) return;

                     const taskStore = (taskBoardRef.current?.instance?.project as any)?.taskStore;
                     if (!taskStore) return;

                     tearsheetPersonToTaskCardFields({
                        taskStore,
                        person,
                        resourceId,
                        lastNameFirst,
                        taskBoardRef,
                     });
                  } catch {
                     // Person fetch failed -- stale card data is acceptable
                  }
               },
            });
         },
      };
   }
   if (selectedTaskTypes.length === 1) {
      const selectedTaskType = selectedTasks[0].getData("type");

      // Allow ending assignments if all tasks are able to be ended
      if (selectedTaskType === BoardsTaskType.ASSIGNMENT && canManageAssignments) {
         items.endAssignment = {
            text: I18n.t("views.company.workforce_planning.boards.task_menu.end_assignment", {
               count: selectedTasks.length,
            }),
            onItem: () => {
               selectedProjectIds.length === 1
                  ? onTaskEndAssignmentSingleProject({
                       projectId: selectedProjectIds[0],
                       selectedTasks: selectedTasks,
                       selectedDate: selectedDate,
                       taskBoardRef: taskBoardRef,
                       sendAssignmentAlertsTearsheetRef: sendAssignmentAlertsTearsheetRef,
                       canManageAlerts: canManageAlerts,
                       I18n: I18n,
                    })
                  : onTaskEndAssignmentMultipleProjects({
                       selectedTasks: selectedTasks,
                       selectedDate: selectedDate,
                       taskBoardRef: taskBoardRef,
                       I18n: I18n,
                    });
            },
         };

         items.deleteAssignment = {
            text: I18n.t("views.company.workforce_planning.boards.task_menu.delete_assignment", {
               count: selectedTasks.length,
            }),
            onItem: () => {
               selectedProjectIds.length === 1
                  ? onTaskDeleteAssignmentSingleProject({
                       projectId: selectedProjectIds[0],
                       selectedTasks: selectedTasks,
                       taskBoardRef: taskBoardRef,
                       sendAssignmentAlertsTearsheetRef: sendAssignmentAlertsTearsheetRef,
                       canManageAlerts: canManageAlerts,
                       I18n: I18n,
                    })
                  : onTaskDeleteAssignmentMultipleProjects({
                       selectedTasks: selectedTasks,
                       taskBoardRef: taskBoardRef,
                       I18n: I18n,
                    });
            },
         };
      }
   }
}

type OnTaskDblClickParams = Parameters<BryntumFn<"onTaskDblClick">>[0] & {
   assignmentDetailsTearsheetRef: React.RefObject<GanttTearsheetRef>;
   editRequestTearsheetRef: React.RefObject<GanttTearsheetRef>;
   checkAuthAction: (action: AuthAction) => boolean;
   currentUserId: string | null;
   taskBoardRef: RefObject<BryntumTaskBoard>;
};
export const onTaskDblClick: (params: OnTaskDblClickParams) => void = ({
   event,
   taskRecord,
   assignmentDetailsTearsheetRef,
   editRequestTearsheetRef,
   checkAuthAction,
   currentUserId,
   taskBoardRef,
}) => {
   event.preventDefault();

   if (!taskRecord) {
      return;
   }

   const taskType = taskRecord.getData("type");

   // Don't handle double-click for category tasks
   if (taskType === BoardsTaskType.CATEGORY) {
      return;
   }
   const taskId = taskRecord.getData("id");
   const projectId = taskRecord.getData("project_id");
   const creatorId = taskRecord.getData("creator_id");

   // Check permissions based on task type
   let hasPermission = false;

   if (taskType === BoardsTaskType.REQUEST || taskType === BoardsTaskType.EQUIPMENT_REQUEST) {
      // For requests, check if user can manage their own requests or others' requests
      if (creatorId === currentUserId) {
         hasPermission = checkAuthAction(AuthAction.MANAGE_REQUESTS);
      } else {
         hasPermission = checkAuthAction(AuthAction.MANAGE_OTHERS_REQUESTS);
      }
   } else {
      // For assignments (and equipment), check if user can manage assignments
      hasPermission = checkAuthAction(AuthAction.MANAGE_ASSIGNMENTS);
   }

   if (!hasPermission) {
      return;
   }

   // Open the appropriate tearsheet
   if (taskType === BoardsTaskType.REQUEST || taskType === BoardsTaskType.EQUIPMENT_REQUEST) {
      if (!editRequestTearsheetRef.current || !taskBoardRef.current) {
         return;
      }
      editRequestTearsheetRef.current.handleOpenTearsheet({
         projectId,
         requestId: taskId,
         resourceType:
            taskType === BoardsTaskType.REQUEST ? ResourceType.LABOR : ResourceType.EQUIPMENT,
         callback: (data: any) => {
            // Find the task in the taskboard and update it
            const task = (taskBoardRef.current?.instance.project as any)?.taskStore?.getById(
               taskId,
            );
            if (!task) return;

            editedTearsheetRequestToTask(data, task, taskBoardRef);
         },
      });
   } else if (taskType === BoardsTaskType.EQUIPMENT_ASSIGNMENT) {
      if (!assignmentDetailsTearsheetRef.current || !taskBoardRef.current) {
         return;
      }
      assignmentDetailsTearsheetRef.current.handleOpenTearsheet({
         projectId,
         assignmentId: taskId,
         resourceType: ResourceType.EQUIPMENT,
         callback: (data: any) => {
            // Find the task in the taskboard and update it
            const task = (taskBoardRef.current?.instance.project as any)?.taskStore?.getById(
               taskId,
            );
            if (!task) return;

            editedTearsheetEquipmentAssignmentToTask(data, task);
         },
      });
   } else {
      if (!assignmentDetailsTearsheetRef.current || !taskBoardRef.current) {
         return;
      }
      assignmentDetailsTearsheetRef.current.handleOpenTearsheet({
         projectId,
         assignmentId: taskId,
         resourceType: ResourceType.LABOR,
         callback: (data: any) => {
            // Find the task in the taskboard and update it
            const task = (taskBoardRef.current?.instance.project as any)?.taskStore?.getById(
               taskId,
            );
            if (!task) return;

            editedTearsheetAssignmentToTask(data, task);
         },
      });
   }
};

type ShowTransferAssignmentModalParams = {
   assignments: TaskModel[];
   requests: TaskModel[];
   targetColumn: any;
   viewingDay: number;
   newEndDay: number;
   taskBoardRef: RefObject<BryntumTaskBoard>;
   I18n: ReturnType<typeof useI18nContext>;
   sendAssignmentAlertsTearsheetRef: React.RefObject<GanttTearsheetRef>;
   categoryId: string | null;
   subcategoryId: string | null;
   dropWeight?: number;
};
const showTransferAssignmentModal = (params: ShowTransferAssignmentModalParams) => {
   const {
      assignments,
      requests,
      targetColumn,
      viewingDay,
      newEndDay,
      taskBoardRef,
      I18n,
      sendAssignmentAlertsTearsheetRef,
      categoryId,
      subcategoryId,
      dropWeight,
   } = params;
   const formattedDate = formatDateTime(getAttachedDate(viewingDay), {
      locale: I18n.locale,
      isRequiredTimezone: false,
   });
   const body = (
      <>
         <Typography>
            {I18n.t("views.company.workforce_planning.boards.modals.transfer_assignment_body", {
               date: formattedDate,
               target_project_name: targetColumn.getData("text"),
            })}
         </Typography>
         <Typography>
            {I18n.t(
               "views.company.workforce_planning.boards.modals.transfer_assignment_body_confirmation",
            )}
         </Typography>
      </>
   );

   const project = taskBoardRef.current?.instance.project as ProjectModel | undefined;

   showBryntumModal({
      type: BryntumModalType.TransferAssignment,
      modalId: "boards-confirmation-modal-container",
      title: I18n.t("views.company.workforce_planning.boards.modals.transfer_assignment", {
         count: assignments.length,
      }),
      body: body,
      onSave: async () => {
         // Finalize request transfers with category fields
         requests.forEach((request) => {
            request.set({
               project_id: targetColumn.id as string,
               category_id: categoryId,
               subcategory_id: subcategoryId,
            });
         });
         await transferAssignments(
            assignments,
            targetColumn,
            viewingDay,
            newEndDay,
            taskBoardRef,
            categoryId,
            subcategoryId,
            dropWeight,
         );
      },
      onSecondaryOptionClick: async () => {
         // Finalize request transfers with category fields
         requests.forEach((request) => {
            request.set({
               project_id: targetColumn.id as string,
               category_id: categoryId,
               subcategory_id: subcategoryId,
            });
         });
         const newAssignments = await transferAssignments(
            assignments,
            targetColumn,
            viewingDay,
            newEndDay,
            taskBoardRef,
            categoryId,
            subcategoryId,
            dropWeight,
         );
         sendAssignmentAlertsTearsheetRef.current?.handleOpenTearsheet({
            projectId: targetColumn.id as string,
            alertType: "assignment-transfer",
            assignments: newAssignments,
         });
      },
      onCancel: () => {
         if (project) {
            revertTasksToOriginalValues([...assignments, ...requests], project);
         }
      },
   });
};

type OnBeforeTaskDropParams = Parameters<BryntumFn<"onBeforeTaskDrop">>[0];
/**
 * onBeforeTaskDrop is now a "staging" area that prepares for the actual drop handling in onTaskDrop.
 * It stores original values for potential revert, suspends auto-sync, and returns false for
 * cross-project assignment drops to prevent Bryntum's default behavior.
 */
export const onBeforeTaskDrop: (params: OnBeforeTaskDropParams) => boolean | void = ({
   source,
   taskRecords,
}) => {
   // Store original values on all tasks for potential revert in onTaskDrop
   taskRecords.forEach((taskRecord) => {
      taskRecord.set("_origProjectId", taskRecord.getData("project_id"), true);
      taskRecord.set("_origCategoryId", taskRecord.getData("category_id"), true);
      taskRecord.set("_origSubcategoryId", taskRecord.getData("subcategory_id"), true);
      taskRecord.set("_origStartDay", taskRecord.getData("start_day"), true);
      taskRecord.set("_origEndDay", taskRecord.getData("end_day"), true);
      taskRecord.set("_origStartTime", taskRecord.getData("start_time"), true);
      taskRecord.set("_origEndTime", taskRecord.getData("end_time"), true);
   });

   // Suspend auto-sync to prevent Bryntum's default project_id mutation (between
   // onBeforeTaskDrop and onTaskDrop) from triggering a sync. Resumed in onTaskDrop's
   // exit paths: transferAssignments, handleChooseEndDateTransferCallback,
   // revertTasksToOriginalValues, or inline for same-project / requests-only drops.
   (source.project as ProjectModel).suspendAutoSync();

   // Let Bryntum's default behavior proceed for all drops
   // onTaskDrop will handle category updates and cross-project transfer logic
   // DO NOT manually set project_id here - let Bryntum handle the visual move
   // This is important for proper change tracking and revert on cancel
};

type HandleChooseEndDateTransferCallbackParams = {
   data: any;
   requests: TaskModel[];
   assignments: TaskModel[];
   targetColumn: any;
   categoryId: string | null;
   subcategoryId: string | null;
   dropWeight: number | undefined;
   taskBoardRef: RefObject<BryntumTaskBoard>;
};

/**
 * Handles the callback from the choose_end_date transfer tearsheet.
 * Finalizes request transfers, processes new assignments, removes old ones,
 * and sorts the task store by weight.
 */
const handleChooseEndDateTransferCallback = ({
   data,
   requests,
   assignments,
   targetColumn,
   categoryId,
   subcategoryId,
   dropWeight,
   taskBoardRef,
}: HandleChooseEndDateTransferCallbackParams) => {
   try {
      // Finalize request transfers with category fields
      requests.forEach((request) => {
         request.set({
            project_id: targetColumn.id as string,
            category_id: categoryId,
            subcategory_id: subcategoryId,
         });
      });

      data.forEach((newAssn: any) => {
         const originalTaskForResource = assignments.find(
            (origAssn: any) => origAssn.getData("resource_id") === newAssn.resource_id,
         );
         const newTask = createdTearsheetAssignmentToTask(newAssn, originalTaskForResource!);
         // Apply category fields to the new task
         (newTask as any).category_id = categoryId;
         (newTask as any).subcategory_id = subcategoryId;
         // Preserve the drop position weight so the task appears where it was dropped
         if (dropWeight !== undefined) {
            (newTask as any).weight = dropWeight;
         }

         const addedTask = (taskBoardRef.current?.instance as any).addTask(
            targetColumn as any,
            undefined,
            newTask,
         );

         // Bryntum's addTask returns the data, not a model with .set()
         // We need to find the task in the store and use .set() to trigger reactivity
         if (dropWeight !== undefined) {
            const taskStore = (taskBoardRef.current?.instance as any).project?.taskStore;
            const taskId = addedTask?.id || (newTask as any).id;
            if (taskStore && taskId) {
               const taskInStore = taskStore.getById(taskId);
               if (taskInStore && typeof taskInStore.set === "function") {
                  taskInStore.set("weight", dropWeight);
               } else if (taskInStore) {
                  // Fallback to direct assignment
                  taskInStore.weight = dropWeight;
               }
            }
         }
      });

      assignments.forEach((assignment: any) => {
         assignment.remove();
      });

      // Use the previously calculated dropWeight to sort the tasks by weight,
      // this will put the new task card in the correct category without requiring a refresh
      const taskStore = (taskBoardRef.current?.instance as any).project?.taskStore;
      if (taskStore) {
         taskStore.sort({ field: "weight", ascending: true });
      }
   } catch (error) {
      reportError(error as NotifiableError, "Boards Transfer Assignment Callback");
   } finally {
      (taskBoardRef.current?.instance as any).project.acceptChanges();
      (taskBoardRef.current?.instance as any).project.resumeAutoSync();
      sortTasksByJobTitleSequence(taskBoardRef);

      const sourceProjectIds = new Set(
         assignments.map((a: any) => a.getData("_origProjectId") as string),
      );
      sourceProjectIds.forEach((projectId) => publish("column-tasks-changed", { projectId }));
      publish("column-tasks-changed", { projectId: targetColumn.id as string });
   }
};

/**
 * Reverts tasks to their original values stored during onBeforeTaskDrop
 * and resumes auto-sync on the project model.
 */
const revertTasksToOriginalValues = (tasks: TaskModel[], project: ProjectModel) => {
   tasks.forEach((task) => {
      task.set({
         project_id: task.getData("_origProjectId"),
         category_id: task.getData("_origCategoryId"),
         subcategory_id: task.getData("_origSubcategoryId"),
         start_day: task.getData("_origStartDay"),
         end_day: task.getData("_origEndDay"),
         start_time: task.getData("_origStartTime"),
         end_time: task.getData("_origEndTime"),
      });
      task.clearChanges();
   });
   project.resumeAutoSync();
};

/**
 * Finds the category and subcategory at the drop position by looking backwards
 * through the target tasks from the dropped task's position.
 */
const findCategoryAtDropPosition = (
   targetTasks: TaskModel[],
   droppedTaskId: string | number,
): { categoryId: string | null; subcategoryId: string | null } => {
   const droppedTaskIndex = targetTasks.findIndex((t: any) => t.id === droppedTaskId);

   if (droppedTaskIndex !== -1) {
      // Look backwards from the dropped task position to find the nearest category above it
      for (let i = droppedTaskIndex - 1; i >= 0; i--) {
         const taskAbove: any = targetTasks[i];
         if (taskAbove.type === BoardsTaskType.CATEGORY) {
            // Check if this is a subcategory (has a parent_id)
            if (taskAbove.parent_id) {
               return { categoryId: taskAbove.parent_id, subcategoryId: taskAbove.id };
            }
            // Top-level category
            return { categoryId: taskAbove.id, subcategoryId: null };
         }
      }
   }

   // Dropped outside of any category
   return { categoryId: null, subcategoryId: null };
};

type OnTaskDropParams = Parameters<BryntumFn<"onTaskDrop">>[0] & {
   assignmentDetailsTearsheetRef: React.RefObject<GanttTearsheetRef>;
   transferAssignmentsTearsheetRef: React.RefObject<GanttTearsheetRef>;
   sendAssignmentAlertsTearsheetRef: React.RefObject<GanttTearsheetRef>;
   taskBoardRef: RefObject<BryntumTaskBoard>;
   I18n: ReturnType<typeof useI18nContext>;
   tbdWeeks: number;
};
export const onTaskDrop: (params: OnTaskDropParams) => void = ({
   source,
   targetColumn,
   taskRecords,
   transferAssignmentsTearsheetRef,
   sendAssignmentAlertsTearsheetRef,
   taskBoardRef,
   I18n,
   tbdWeeks,
}) => {
   const prjModel = source.project as ProjectModel;
   // Auto-sync was suspended in onBeforeTaskDrop to cover Bryntum's default project_id
   // mutation between the two events. Every exit path below must call resumeAutoSync().
   const task = taskRecords[0];
   const targetColumnId = targetColumn.getData("id");
   // Use targetColumn.tasks to get tasks in their proper visual order (sorted by weight)
   // This is more reliable than filtering source.tasks which may not be sorted
   const updatedTargetTasks = (targetColumn as any).tasks || [];

   // Determine if this is a cross-project drop using original values stored in onBeforeTaskDrop
   const origProjectId = task.getData("_origProjectId");
   const isCrossProjectDrop = origProjectId !== targetColumnId;

   // Determine category context by looking backwards from the dropped task position
   const { categoryId, subcategoryId } = findCategoryAtDropPosition(updatedTargetTasks, task.id);

   // Find the index of the dropped task for weight calculation
   const droppedTaskIndex = updatedTargetTasks.findIndex((t: any) => t.id === task.id);

   // Yes, this seems ridiculous but it is seemingly the only way to get Bryntum to add a new card in the correct position.
   // Calculate the correct weight based on neighboring tasks at the drop position.
   // We can't rely on task.weight being updated yet, so we calculate it ourselves.
   // Helper to get weight from a task - try multiple access methods
   const getTaskWeight = (t: any): number | null => {
      if (!t) return null;
      // Try different ways to access weight - bryntum can be a bit unpredictable with weight
      const w = t.weight ?? t.data?.weight ?? t.getData?.("weight");
      return typeof w === "number" ? w : null;
   };

   let dropWeight: number | undefined;
   if (droppedTaskIndex !== -1) {
      const taskBefore = droppedTaskIndex > 0 ? updatedTargetTasks[droppedTaskIndex - 1] : null;
      const taskAfter =
         droppedTaskIndex < updatedTargetTasks.length - 1
            ? updatedTargetTasks[droppedTaskIndex + 1]
            : null;

      const weightBefore = getTaskWeight(taskBefore);
      const weightAfter = getTaskWeight(taskAfter);

      if (weightBefore !== null && weightAfter !== null) {
         // Insert between two tasks - use midpoint
         dropWeight = (weightBefore + weightAfter) / 2;
      } else if (weightBefore !== null) {
         // No task after - add 100 to the previous task's weight
         dropWeight = weightBefore + 100;
      } else if (weightAfter !== null) {
         // No task before - subtract 100 from the next task's weight
         dropWeight = weightAfter - 100;
      }
      // If no neighbors have weights, leave dropWeight undefined (will use default)
   }

   const changesType = localStorage.getItem("boards-schedule-changes");
   const viewingDay = (prjModel as any).transport.load.params.day_filter;

   // Handle CROSS-PROJECT drops (transfers)
   if (isCrossProjectDrop) {
      const assignments = taskRecords.filter(
         (t: any) => t.getData("type") === BoardsTaskType.ASSIGNMENT,
      );
      const requests = taskRecords.filter(
         (t: any) =>
            t.getData("type") === BoardsTaskType.REQUEST ||
            t.getData("type") === BoardsTaskType.EQUIPMENT_REQUEST,
      );
      // Restore original project_id and category fields on all assignments.
      // Bryntum's default drop behavior already moved them to the target column,
      // but the originals should keep their source project identity for truncation/sync.
      assignments.forEach((taskRecord) => {
         taskRecord.set({
            project_id: taskRecord.getData("_origProjectId"),
            category_id: taskRecord.getData("_origCategoryId"),
            subcategory_id: taskRecord.getData("_origSubcategoryId"),
         });
      });

      const revertAllTasks = () => revertTasksToOriginalValues(taskRecords, prjModel);

      // If only requests (no assignments), just update project_id and category fields
      if (assignments.length === 0 && requests.length > 0) {
         requests.forEach((request) => {
            request.set({
               project_id: targetColumn.id as string,
               category_id: categoryId,
               subcategory_id: subcategoryId,
            });
         });
         prjModel.resumeAutoSync();
         return;
      }

      // For assignments, handle based on schedule changes type
      switch (changesType) {
         case "choose_end_date": {
            const tearsheetAssignments = assignments.map((assignment: any) => ({
               id: assignment.id,
               resourceId: assignment.getData("resource_id"),
               resourceName: assignment.getData("name"),
               type: assignment.getData("type"),
               recipientIds: [assignment.getData("resource_id")],
               workDays: assignment.getData("work_days"),
               startDay: assignment.getData("start_day"),
               endDay: assignment.getData("end_day"),
               categoryId: categoryId,
               subcategoryId: subcategoryId,
            }));

            transferAssignmentsTearsheetRef.current?.handleOpenTearsheet({
               projectId: targetColumn.id as string,
               assignments: tearsheetAssignments,
               endDay: viewingDay,
               categoryId: categoryId,
               subcategoryId: subcategoryId,
               callback: (data: any) => {
                  handleChooseEndDateTransferCallback({
                     data,
                     requests,
                     assignments,
                     targetColumn,
                     categoryId,
                     subcategoryId,
                     dropWeight,
                     taskBoardRef,
                  });
               },
               onClose: () => {
                  // Revert all tasks to original values on cancel/close
                  revertAllTasks();
               },
            });
            return;
         }

         case "single_day": {
            const newEndDay = viewingDay;
            showTransferAssignmentModal({
               assignments,
               requests,
               targetColumn,
               viewingDay,
               newEndDay,
               taskBoardRef,
               I18n,
               sendAssignmentAlertsTearsheetRef,
               categoryId,
               subcategoryId,
               dropWeight,
            });
            return;
         }

         case "project_end_date":
         default: {
            const estEndDate = targetColumn.getData("est_end_date");
            const workDays = task.getData("work_days");
            let newEndDay: number;

            if (estEndDate) {
               const endDate = getAttachedDate(estEndDate);
               // If the project end date falls on a non-work day, walk back to the last work day
               if (!workDays[endDate.getDay() as keyof typeof workDays]) {
                  const lastWorkDay = getLastWorkDate({ fromDate: endDate, workDays });
                  newEndDay = lastWorkDay ? getDetachedDay(lastWorkDay) : estEndDate;
               } else {
                  newEndDay = estEndDate;
               }
            } else {
               newEndDay = getDetachedDay(
                  getLastWorkDateInNumberOfWeeks({
                     workDays,
                     startDate: getAttachedDate(task.getData("_origStartDay")),
                     numberOfWeeks: tbdWeeks,
                  }),
               );
            }

            showTransferAssignmentModal({
               assignments,
               requests,
               targetColumn,
               viewingDay,
               newEndDay,
               taskBoardRef,
               I18n,
               sendAssignmentAlertsTearsheetRef,
               categoryId,
               subcategoryId,
               dropWeight,
            });
            return;
         }
      }
   }

   // Handle SAME-PROJECT drops (category changes only - no date manipulation)
   // Update all dropped tasks with the new category fields
   taskRecords.forEach((taskRecord) => {
      const origCategoryId = taskRecord.getData("_origCategoryId");
      const origSubcategoryId = taskRecord.getData("_origSubcategoryId");

      taskRecord.set({
         category_id: categoryId,
         subcategory_id: subcategoryId,
      });

      // Explicitly mark category fields as modified when changing to/from null
      // Bryntum's change detection may not track null value changes properly
      if (categoryId !== origCategoryId) {
         (taskRecord as any).meta.modified.category_id = origCategoryId;
      }
      if (subcategoryId !== origSubcategoryId) {
         (taskRecord as any).meta.modified.subcategory_id = origSubcategoryId;
      }
   });

   // Resume auto-sync and let Bryntum sync the changes
   prjModel.resumeAutoSync();

   sortTasksByJobTitleSequence(taskBoardRef);
};

export const handleTaskClick = (
   params: Parameters<BryntumFn<"onTaskClick">>[0],
   selectedRequestRef: React.MutableRefObject<any>,
   setSelectedRequest: (request: any) => void,
   setIsBenchPanelOpen: (isOpen: boolean) => void,
   taskBoardRef: RefObject<BryntumTaskBoard>,
) => {
   const { taskRecord, event } = params;

   // Don't handle click for category tasks
   if (taskRecord.getData("type") === BoardsTaskType.CATEGORY) {
      return;
   }

   const isMultiSelectClick = event?.metaKey || event?.ctrlKey || event?.altKey || event?.shiftKey;

   // Do not open smart bench is user is doing mutli-select to drag multiple cards
   if (isMultiSelectClick) {
      return;
   }

   // Do not open smart bench if the request has already started
   const startDay = (taskRecord as any)?.start_day;
   if (startDay) {
      const requestStartDate = convertDetachedDayToUtcDateWithoutTimezoneOffset(startDay);
      if (requestStartDate?.getTime() <= new Date().getTime()) {
         return;
      }
   }

   const taskboardElement = taskBoardRef.current?.element;
   // If user clicks the same card again, close the smart bench
   if (selectedRequestRef.current?.id === taskRecord?.id) {
      removeSelectedRequestStyles(taskboardElement, taskRecord.id as string);
      setIsBenchPanelOpen(false);
      selectedRequestRef.current = null;
      setSelectedRequest(null);
   } else {
      // Open the smart bench if user clicks a new or different card
      if (taskboardElement) {
         const allCards = taskboardElement.querySelectorAll(".b-task-board-card");
         allCards.forEach((card) => {
            card.classList.remove("task-card-selected");
         });
      }

      if ((taskRecord as any).type === "request") {
         if (taskboardElement) {
            const selectedCard = taskboardElement.querySelector(`[data-task="${taskRecord.id}"]`);
            if (selectedCard) {
               selectedCard.classList.add("task-card-selected");
            }
         }
         selectedRequestRef.current = taskRecord;
         setSelectedRequest(taskRecord);
         setIsBenchPanelOpen(true);
      }
   }
};

export const removeSelectedRequestStyles = (
   taskboardElement: HTMLElement | undefined,
   taskId: string,
) => {
   const selectedCard = taskboardElement?.querySelector(`[data-task="${taskId}"]`);
   if (selectedCard) {
      selectedCard.classList.remove("task-card-selected");
   }
};

// Since we're using Bryntum's built-in load/sync, we need to manually renew session on a 401.
export const renewSession = async (
   responseCode: string,
   retryCount: MutableRefObject<number>,
   callback: () => void | Promise<void>,
) => {
   if (retryCount.current == null || retryCount.current >= 1) {
      return;
   }
   retryCount.current++;
   if (responseCode.toLowerCase() === "unauthorized") {
      try {
         await SessionStore.renewSession().payload;
      } catch (error) {
         AuthManager2.navigateToSignIn();
         throw error;
      }
      await callback();
   }
};
